import { useMutation } from '@tanstack/react-query'
import { createApiCall } from 'services'

type UseCertificateDeleteMutationsResult = [
  {
    remove: (payload: { id: string }) => void
    bulkRemove: (payload: { ids: string[] }) => void
  },
  {
    isLoading: boolean
    isError: boolean
  }
]

type UseCertificateDeleteMutationsOptions = {
  onSuccess?: (data?: any) => void
  onError?: (error?: any) => void
}

// VRG-231 review round 2 item 9 — this hook is the plain single-certificate
// delete (`DELETE /certificates/admin/:certificateId`, no concept of a group).
// Deleting a certificate group's tagless PARENT goes through the dedicated
// `useCertificateGroupDelete` hook instead (`DELETE
// /certificates/admin/groups/:certificateId`, which accepts `childIdsToDelete`)
// — see DeleteCertificateModal.tsx for the branch that picks between the two.
export const useCertificateDeleteMutations = (
  options?: UseCertificateDeleteMutationsOptions
): UseCertificateDeleteMutationsResult => {
  // Single delete certificate
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await createApiCall({
        method: 'DELETE',
        url: `/certificates/admin/${id}`,
      })
      return id
    },
    onSuccess: (id) => {
      options?.onSuccess?.(id)
    },
    onError: (error: any) => {
      options?.onError?.(error)
    },
  })

  // Bulk delete certificates
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/certificates/admin/bulk-delete`,
        data: { ids },
      })
      return response.data
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      options?.onError?.(error)
    },
  })

  const remove = (payload: { id: string }) => {
    deleteMutation.mutate(payload.id)
  }

  const bulkRemove = (payload: { ids: string[] }) => {
    bulkDeleteMutation.mutate(payload.ids)
  }

  return [
    {
      remove,
      bulkRemove,
    },
    {
      isLoading: deleteMutation.isPending || bulkDeleteMutation.isPending,
      isError: deleteMutation.isError || bulkDeleteMutation.isError,
    },
  ]
}
